import React, { useCallback, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { PreviewPanel } from '@atlassian/preview-panel/preview-panel';
import { PreviewPanelBody } from '@atlassian/preview-panel/preview-panel-body';
import { PreviewPanelHeader } from '@atlassian/preview-panel/preview-panel-header';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api/use-preview-panel-actions';

import { withSmartCardProviderContext } from '@confluence/editor-features/entry-points/withSmartCardProviderContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { BylineContentType } from '../utils/contentAri.ts';

import { PANEL_TYPE, PARENT_PRODUCT } from './previewPanelConstants.ts';
import { useConnectionSectionsData } from './sections/useConnectionSectionsData.ts';
import { TeamworkGraphPreviewPanelContextColumn } from './TeamworkGraphPreviewPanelContextColumn.tsx';
import { TeamworkGraphPreviewPanelFooter } from './TeamworkGraphPreviewPanelFooter.tsx';
import { TeamworkGraphPreviewPanelInfoButton } from './TeamworkGraphPreviewPanelInfoButton.tsx';
import { TeamworkGraphPreviewPanelTitle } from './TeamworkGraphPreviewPanelTitle.tsx';
import { useTeamworkGraphPreviewPanelMode } from './useTeamworkGraphPreviewPanelMode.ts';
import { TeamworkGraphChatColumn } from './visualization/TeamworkGraphChatColumn.tsx';
import { TeamworkGraphShareContextButton } from './visualization/TeamworkGraphShareContextButton.tsx';

const styles = cssMap({
	// Keep scrolling inside the body so connections cannot cover the footer.
	scroll: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minHeight: '0px',
		overflowY: 'auto',
	},
	// The scroller is a flex column, so a shrinkable body collapses to the viewport and the
	// connections overflow past its bottom padding. `height: auto` undoes the `100%` that
	// `PreviewPanelBody` sets under `ads-panel-system-gpp`, which has the same effect.
	body: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		flexShrink: 0,
		height: 'auto',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
	},
	// Rovo chat brings its own padding and needs to reach the panel's edges, so this drops
	// the body's insets. `minHeight: 0` is the bounded-height link the chat needs.
	chatBody: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		minHeight: '0px',
	},
});

export type TeamworkGraphPreviewPanelProps = {
	contentId?: string;
	/** Keys the panel's own graph queries (people) and the modal's canvas on the content's ARI. */
	contentType?: BylineContentType;
};

export function TeamworkGraphPreviewPanelInner({
	contentId,
	contentType,
}: TeamworkGraphPreviewPanelProps): React.JSX.Element {
	const { close } = usePreviewPanelsActions();
	// The panel is only rendered while it is open, so its sections fetch as soon as it
	// mounts — no `enabled` gate, unlike the modal.
	const connections = useConnectionSectionsData(contentId, contentType);
	const { isRovoLLMEnabled } = useSessionData();
	const { isRovo, prefilledPrompt, showRovo, showConnections } = useTeamworkGraphPreviewPanelMode({
		isRovoEnabled: isRovoLLMEnabled,
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const openModal = useCallback(() => setIsModalOpen(true), []);
	const closeModal = useCallback(() => setIsModalOpen(false), []);
	// Close first: Rovo unmounts the column that mounts the modal, so an open flag left behind
	// would pop the modal back up on the way back to connections.
	const showRovoFromPanel = useCallback(
		(...args: Parameters<typeof showRovo>) => {
			setIsModalOpen(false);
			showRovo(...args);
		},
		[showRovo],
	);
	// Bound here, not in the header button, so the trigger stays a wiring-point concern like the others.
	const showRovoFromShareContext = useCallback(
		(prompt: string) => showRovoFromPanel('shareContext', prompt),
		[showRovoFromPanel],
	);

	return (
		<PreviewPanel testId="teamwork-graph-preview-panel">
			<PreviewPanelHeader
				onClose={() => close()}
				analyticsClient={null}
				parentProduct={PARENT_PRODUCT}
				panelType={PANEL_TYPE}
				moreActions={
					!isRovo && (
						<TeamworkGraphShareContextButton
							contentId={contentId}
							testId="teamwork-graph-preview-panel-share-context"
							onAskRovo={isRovoLLMEnabled ? showRovoFromShareContext : undefined}
						/>
					)
				}
			>
				<Inline alignBlock="center" space="space.050">
					<TeamworkGraphPreviewPanelTitle />
					<TeamworkGraphPreviewPanelInfoButton />
				</Inline>
			</PreviewPanelHeader>
			<Box xcss={styles.scroll} testId="teamwork-graph-preview-panel-scroll">
				<PreviewPanelBody
					analyticsClient={null}
					parentProduct={PARENT_PRODUCT}
					panelType={PANEL_TYPE}
					styles={isRovo ? styles.chatBody : styles.body}
				>
					{isRovo ? (
						<TeamworkGraphChatColumn onBack={showConnections} prefilledPrompt={prefilledPrompt} />
					) : (
						<TeamworkGraphPreviewPanelContextColumn
							contentId={contentId}
							contentType={contentType}
							connections={connections}
							isRovoEnabled={isRovoLLMEnabled}
							onAskRovo={showRovoFromPanel}
							isModalOpen={isModalOpen}
							onCloseModal={closeModal}
						/>
					)}
				</PreviewPanelBody>
			</Box>
			{/* The chat composer replaces the footer in chat mode. */}
			{!isRovo && (
				<TeamworkGraphPreviewPanelFooter
					onOpenGraph={openModal}
					testId="teamwork-graph-preview-panel-footer"
				/>
			)}
		</PreviewPanel>
	);
}

// `PreviewPanelContainer` renders the panel from `PageLayout`, outside the
// byline's own SmartCard provider, so the panel needs its own — the connection
// rows call `useSmartLinkActions` / `useSmartLinkReload`, which throw without one.
// Being its own tree, it is outside the byline's trace-id wrapper and needs one of its own.
export const TeamworkGraphPreviewPanel: (
	props: TeamworkGraphPreviewPanelProps,
) => React.JSX.Element = withSmartCardProviderContext(TeamworkGraphPreviewPanelInner);
